import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { AI_ASSISTANT_AVATAR_URL } from '../../../shared/constants/aiAssistantAvatar';
import { Conversation } from '../types/Conversation';
import { isEmployeeAiAssistantDirectConversation } from '../utils/conversationClassification';
import { useAiVoiceSession } from '../hooks/useAiVoiceSession';
import styles from './AiVoiceCallControl.module.css';

type AiVoiceCallControlProps = {
  conversation: Conversation | null;
  variant?: 'header' | 'floating';
};

const statusLabelKey = (
  status: string,
  options: { duplex: boolean; micPaused: boolean },
): string => {
  if (options.micPaused && status !== 'error' && status !== 'connecting') {
    return 'voiceMicPaused';
  }

  switch (status) {
    case 'connecting':
      return 'voiceConnecting';
    case 'recording':
      return options.duplex ? 'voiceHearingYou' : 'voiceListening';
    case 'processing':
      return 'voiceProcessing';
    case 'speaking':
      return 'voiceSpeaking';
    case 'error':
      return 'voiceError';
    default:
      return options.duplex ? 'voiceReadyDuplex' : 'voiceReady';
  }
};

const MinimizeIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M5 10h10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);

const ExpandIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M7 4H4v3M13 4h3v3M4 13v3h3M16 13v3h-3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PhoneIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path
      d="M8.2 3.8c.6-.3 1.3 0 1.6.6l1 2.1c.2.5.1 1.1-.3 1.5l-.8.8c.6 1.3 1.6 2.3 2.9 2.9l.8-.8c.4-.4 1-.5 1.5-.3l2.1 1c.6.3.9 1 .6 1.6l-.7 1.5c-.3.7-1 1.2-1.8 1.2-4.7.1-8.9-3.9-9.5-8.6 0-.8.5-1.5 1.2-1.8L8.2 3.8Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
  </svg>
);

export const AiVoiceCallControl: React.FC<AiVoiceCallControlProps> = ({
  conversation,
  variant = 'header',
}) => {
  const { t } = useTranslation('conversations');
  const eligible = Boolean(conversation && isEmployeeAiAssistantDirectConversation(conversation));
  const voice = useAiVoiceSession(eligible ? conversation?.id ?? null : null, eligible);
  const { hangUp, isOpen, isDuplex, micPaused, level, status, turns, isQuotaExceeded, capabilities } = voice;
  const [minimized, setMinimized] = useState(false);
  const threadRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setMinimized(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const node = threadRef.current;
    if (!node || minimized) {
      return;
    }
    node.scrollTop = node.scrollHeight;
  }, [turns, minimized, status]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !minimized) {
        setMinimized(true);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, minimized]);

  if (!eligible || !voice.isAvailable) {
    return null;
  }

  const statusText = t(statusLabelKey(status, { duplex: isDuplex, micPaused }));
  const quotaBlocked = isQuotaExceeded && !isOpen;
  const triggerLabel = quotaBlocked
    ? t(capabilities?.aiTokenQuotaExceeded ? 'aiTokenQuotaExceeded' : 'voiceTokenQuotaExceeded')
    : (isOpen ? t('voiceExpand') : t('startVoiceCall'));

  const overlay = isOpen
    ? createPortal(
      minimized ? (
        <div className={styles.dock} role="dialog" aria-label={t('assistant')}>
          <div
            className={styles.avatarWrap}
            data-status={status}
            data-paused={micPaused ? 'true' : 'false'}
            style={{ ['--voice-level' as string]: String(level) }}
          >
            <img src={AI_ASSISTANT_AVATAR_URL} alt="" className={styles.avatar} />
          </div>
          <div className={styles.dockCopy}>
            <strong>{t('assistant')}</strong>
            <span data-status={status}>{statusText}</span>
          </div>
          <button
            type="button"
            className={styles.iconButton}
            onClick={() => setMinimized(false)}
            title={t('voiceExpand')}
            aria-label={t('voiceExpand')}
          >
            <ExpandIcon />
          </button>
          <button
            type="button"
            className={`${styles.iconButton} ${styles.iconButtonDanger}`}
            onClick={hangUp}
            title={t('endVoiceCall')}
            aria-label={t('endVoiceCall')}
          >
            <PhoneIcon />
          </button>
        </div>
      ) : (
        <div className={styles.overlay} role="presentation">
          <div
            className={styles.card}
            role="dialog"
            aria-modal="true"
            aria-labelledby="ai-voice-call-title"
          >
            <div className={styles.header}>
              <div
                className={styles.avatarWrap}
                data-status={status}
                data-paused={micPaused ? 'true' : 'false'}
                style={{ ['--voice-level' as string]: String(level) }}
              >
                <img src={AI_ASSISTANT_AVATAR_URL} alt="" className={styles.avatar} />
              </div>
              <div className={styles.headerCopy}>
                <div className={styles.liveRow}>
                  <h2 id="ai-voice-call-title" className={styles.title}>{t('assistant')}</h2>
                  {status !== 'error' && status !== 'connecting' ? (
                    <span className={styles.liveBadge}>{t('voiceLive')}</span>
                  ) : null}
                </div>
                <p className={styles.status} data-status={status}>{statusText}</p>
              </div>
              <div className={styles.headerActions}>
                <button
                  type="button"
                  className={styles.iconButton}
                  onClick={() => setMinimized(true)}
                  title={t('voiceMinimize')}
                  aria-label={t('voiceMinimize')}
                >
                  <MinimizeIcon />
                </button>
                <button
                  type="button"
                  className={`${styles.iconButton} ${styles.iconButtonDanger}`}
                  onClick={hangUp}
                  title={t('endVoiceCall')}
                  aria-label={t('endVoiceCall')}
                >
                  <PhoneIcon />
                </button>
              </div>
            </div>

            <div className={styles.thread} ref={threadRef}>
              {turns.length === 0 ? (
                <p className={styles.empty}>{t('voiceEmptyTranscript')}</p>
              ) : (
                turns.map(turn => (
                  <div
                    key={turn.id}
                    className={`${styles.bubble} ${turn.role === 'user' ? styles.bubbleUser : styles.bubbleAssistant}`}
                    data-streaming={turn.final ? 'false' : 'true'}
                  >
                    <span className={styles.bubbleLabel}>
                      {turn.role === 'user' ? t('voiceYouSaid') : t('assistant')}
                    </span>
                    <p className={styles.bubbleText}>{turn.text}</p>
                  </div>
                ))
              )}
            </div>

            {voice.error ? <p className={styles.error}>{voice.error}</p> : null}

            <div className={styles.footer}>
              {isDuplex ? (
                <button
                  type="button"
                  className={`${styles.talkButton} ${micPaused ? '' : styles.talkButtonLive}`}
                  onClick={voice.toggleMicPaused}
                >
                  {micPaused ? t('voiceResumeMic') : t('voicePauseMic')}
                </button>
              ) : (
                <button
                  type="button"
                  className={`${styles.talkButton} ${status === 'recording' ? styles.talkButtonActive : ''}`}
                  disabled={status === 'connecting' || status === 'processing'}
                  onPointerDown={event => {
                    if (event.button !== 0) {
                      return;
                    }
                    event.preventDefault();
                    event.currentTarget.setPointerCapture(event.pointerId);
                    void voice.startTalking();
                  }}
                  onPointerUp={() => voice.stopTalking()}
                  onPointerCancel={() => voice.stopTalking()}
                >
                  {status === 'recording' ? t('voiceReleaseToSend') : t('voiceHoldToTalk')}
                </button>
              )}
              <button type="button" className={styles.hangUpButton} onClick={hangUp}>
                {t('endVoiceCall')}
              </button>
            </div>
          </div>
        </div>
      ),
      document.body,
    )
    : null;

  return (
    <>
      <button
        type="button"
        className={variant === 'floating' ? styles.floatingTrigger : styles.headerTrigger}
        onClick={() => {
          if (quotaBlocked) {
            return;
          }
          if (!voice.isOpen) {
            void voice.startCall();
            return;
          }
          setMinimized(false);
        }}
        disabled={quotaBlocked}
        aria-label={triggerLabel}
        title={triggerLabel}
        aria-pressed={voice.isOpen}
      >
        <PhoneIcon />
      </button>
      {overlay}
    </>
  );
};
